class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    window.KronoxCartDrawerEnhancer?.enhance();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      window.KronoxCartDrawerEnhancer?.enhance();
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

const kpxCartDrawerStyles = `
  cart-drawer.kpx-cart {
    --kpx-cart-orange: #ff5a00;
    --kpx-cart-bg: #050505;
    --kpx-cart-panel: #121416;
    --kpx-cart-border: #282d31;
    --kpx-cart-text: #ffffff;
    --kpx-cart-muted: #b8bbc0;
    overflow-x: hidden;
  }

  cart-drawer.kpx-cart,
  cart-drawer.kpx-cart * {
    box-sizing: border-box;
  }

  cart-drawer.kpx-cart .drawer__inner {
    width: min(44rem, 100vw);
    max-width: 100vw;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    overscroll-behavior: contain;
    background: linear-gradient(180deg, rgba(255, 90, 0, 0.05), rgba(255, 255, 255, 0.012) 18%, rgba(0, 0, 0, 0) 48%), var(--kpx-cart-bg);
    color: var(--kpx-cart-text);
    border-left: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: -28px 0 70px rgba(0, 0, 0, 0.55);
  }

  cart-drawer.kpx-cart .drawer__header {
    position: sticky;
    top: 0;
    z-index: 4;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 12px;
    padding: 18px 18px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(5, 5, 5, 0.96);
    backdrop-filter: blur(16px);
  }

  cart-drawer.kpx-cart .drawer__heading {
    margin: 0;
    color: #fff;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 26px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: 0;
  }

  cart-drawer.kpx-cart .drawer__close {
    color: #fff;
  }

  .kpx-cart__timer {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    gap: 3px;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    text-align: right;
    white-space: nowrap;
  }

  .kpx-cart__timer span {
    color: var(--kpx-cart-muted);
    font-size: 12px;
    line-height: 1;
    font-weight: 800;
  }

  .kpx-cart__timer strong {
    color: var(--kpx-cart-orange);
    font-size: 16px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: 0.04em;
  }

  cart-drawer.kpx-cart cart-drawer-items {
    display: none !important;
  }

  .kpx-cart__stack {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px 16px 16px;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-y;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 90, 0, 0.75) rgba(255, 255, 255, 0.08);
  }

  .kpx-cart__stack > * {
    flex-shrink: 0;
  }

  .kpx-cart__stack.is-empty {
    justify-content: center;
  }

  .kpx-cart__timer[hidden],
  .kpx-cart__confirm[hidden],
  .kpx-cart__items[hidden],
  .kpx-cart__panel[hidden],
  .kpx-cart__trust[hidden],
  cart-drawer.kpx-cart .drawer__footer[hidden],
  cart-drawer.kpx-cart.is-empty .drawer__footer {
    display: none !important;
  }

  .kpx-cart__confirm {
    display: flex;
    align-items: center;
    gap: 9px;
    min-height: 72px;
    padding: 12px 14px;
    border: 1.5px solid rgba(101, 216, 74, 0.42);
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(101, 216, 74, 0.13), rgba(101, 216, 74, 0.07));
    color: #edf7eb;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 13px;
    line-height: 1.2;
    font-weight: 850;
  }

  .kpx-cart__confirm i {
    position: relative;
    width: 22px;
    height: 22px;
    flex: 0 0 22px;
    border-radius: 999px;
    background: #65d84a;
    box-shadow: 0 0 0 5px rgba(101, 216, 74, 0.12);
  }

  .kpx-cart__confirm i::after {
    content: "";
    position: absolute;
    left: 7px;
    top: 5px;
    width: 7px;
    height: 11px;
    border: solid #071007;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  .kpx-cart__panel {
    overflow: hidden;
    min-width: 0;
    max-width: 100%;
    border: 1px solid var(--kpx-cart-border);
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.008)), var(--kpx-cart-panel);
    color: #fff;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .kpx-cart__upgrade {
    display: grid;
    grid-template-columns: 66px minmax(0, 1fr);
    gap: 12px;
    padding: 13px;
    border-color: rgba(255, 90, 0, 0.75);
    background: linear-gradient(135deg, rgba(255, 90, 0, 0.16), rgba(255, 255, 255, 0.015)), #171312;
    box-shadow: inset 0 0 0 1px rgba(255, 90, 0, 0.18);
  }

  .kpx-cart__upgrade[hidden] {
    display: none;
  }

  .kpx-cart__upgrade-media {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 66px;
    border-radius: 8px;
    background: #070707;
    color: var(--kpx-cart-orange);
  }

  .kpx-cart__upgrade-media svg {
    width: 38px;
    height: 38px;
  }

  .kpx-cart__eyebrow {
    display: block;
    margin-bottom: 4px;
    color: var(--kpx-cart-orange);
    font-size: 11px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .kpx-cart__upgrade strong {
    display: block;
    color: #fff;
    font-size: 15px;
    line-height: 1.12;
    font-weight: 950;
  }

  .kpx-cart__upgrade p {
    margin: 5px 0 0;
    color: var(--kpx-cart-muted);
    font-size: 12px;
    line-height: 1.28;
    font-weight: 700;
  }

  .kpx-cart__upgrade-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 10px;
  }

  .kpx-cart__upgrade-price {
    color: #fff;
    font-size: 13px;
    font-weight: 950;
    white-space: nowrap;
  }

  .kpx-cart__upgrade-button {
    min-height: 34px;
    padding: 0 13px;
    border: 0;
    border-radius: 7px;
    background: var(--kpx-cart-orange);
    color: #fff;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 12px;
    font-weight: 950;
    cursor: pointer;
  }

  .kpx-cart__upgrade-button[disabled] {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .kpx-cart__items {
    display: grid;
    gap: 9px;
  }

  .kpx-cart__item {
    display: grid;
    grid-template-columns: 74px minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    padding: 12px;
  }

  .kpx-cart__item-img {
    width: 74px;
    height: 74px;
    object-fit: contain;
    border-radius: 8px;
    background: #070707;
  }

  .kpx-cart__item-copy {
    min-width: 0;
  }

  .kpx-cart__item-copy strong {
    display: block;
    color: #fff;
    font-size: 15px;
    line-height: 1.14;
    font-weight: 950;
  }

  .kpx-cart__item-copy span {
    display: block;
    margin-top: 5px;
    color: var(--kpx-cart-muted);
    font-size: 12px;
    line-height: 1.2;
    font-weight: 750;
  }

  .kpx-cart__item-count {
    color: var(--kpx-cart-muted);
    font-size: 11px;
    line-height: 1;
    font-weight: 800;
    text-align: center;
  }

  .kpx-cart__item-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 7px;
    white-space: nowrap;
  }

  .kpx-cart__item-price {
    color: #fff;
    font-size: 15px;
    line-height: 1;
    font-weight: 950;
  }

  .kpx-cart__item-qty {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 34px;
    height: 28px;
    border: 1px solid #30343a;
    border-radius: 999px;
    color: #cfd2d7;
    font-size: 12px;
    font-weight: 900;
  }

  .kpx-cart__item-remove {
    border: 0;
    padding: 0;
    background: transparent;
    color: #9aa0a8;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 11px;
    line-height: 1;
    font-weight: 850;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
  }

  .kpx-cart__empty {
    width: 100%;
    padding: 34px 18px;
    text-align: center;
  }

  .kpx-cart__empty[hidden] {
    display: none;
  }

  .kpx-cart__empty strong {
    display: block;
    color: #fff;
    font-size: 22px;
    line-height: 1.1;
    font-weight: 950;
  }

  .kpx-cart__empty span {
    display: block;
    margin-top: 8px;
    color: var(--kpx-cart-muted);
    font-size: 13px;
    line-height: 1.35;
    font-weight: 750;
  }

  .kpx-cart__empty-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 46px;
    margin-top: 20px;
    padding: 0 24px;
    border-radius: 8px;
    background: var(--kpx-cart-orange);
    color: #fff;
    font-size: 14px;
    line-height: 1;
    font-weight: 950;
    text-decoration: none;
    text-transform: uppercase;
  }

  .kpx-cart__progress {
    padding: 13px;
  }

  .kpx-cart__progress strong {
    display: block;
    color: #fff;
    font-size: 15px;
    line-height: 1.12;
    font-weight: 950;
  }

  .kpx-cart__progress p {
    margin: 5px 0 0;
    color: var(--kpx-cart-muted);
    font-size: 12px;
    line-height: 1.28;
    font-weight: 700;
  }

  .kpx-cart__bar {
    overflow: hidden;
    height: 8px;
    margin: 11px 0 8px;
    border-radius: 999px;
    background: #2b2f34;
  }

  .kpx-cart__bar span {
    display: block;
    width: 0%;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #ff5a00, #ff8a2a);
    transition: width 0.35s ease;
  }

  .kpx-cart__milestones {
    display: flex;
    justify-content: space-between;
    color: #8f949c;
    font-size: 10px;
    font-weight: 800;
  }

  .kpx-cart__milestones span.is-active {
    color: #fff;
  }

  .kpx-cart__trust {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .kpx-cart__trust-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 11px 6px;
    border: 1px solid var(--kpx-cart-border);
    border-radius: 8px;
    background: #101214;
    color: #fff;
    text-align: center;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 10px;
    line-height: 1.15;
    font-weight: 900;
    text-transform: uppercase;
  }

  .kpx-cart__trust-item svg {
    width: 21px;
    height: 21px;
  }

  cart-drawer.kpx-cart .drawer__footer {
    position: relative;
    z-index: 5;
    flex: 0 0 auto;
    padding: 15px 16px 18px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(5, 5, 5, 0.97);
    backdrop-filter: blur(16px);
  }

  cart-drawer.kpx-cart .totals {
    margin-bottom: 10px;
  }

  cart-drawer.kpx-cart .totals__total,
  cart-drawer.kpx-cart .totals__total-value {
    color: #fff;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 16px;
    line-height: 1;
    font-weight: 950;
  }

  cart-drawer.kpx-cart .tax-note {
    display: none;
  }

  .kpx-cart__savings {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: -2px 0 12px;
    padding: 9px 11px;
    border: 1px solid rgba(101, 216, 74, 0.24);
    border-radius: 8px;
    background: rgba(101, 216, 74, 0.075);
    color: #e9f8e7;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 12px;
    line-height: 1;
    font-weight: 900;
  }

  .kpx-cart__savings[hidden] {
    display: none !important;
  }

  .kpx-cart__savings strong {
    color: #65d84a;
    font-size: 13px;
    font-weight: 950;
    white-space: nowrap;
  }

  cart-drawer.kpx-cart .cart__checkout-button {
    min-height: 54px;
    border-radius: 8px;
    background: var(--kpx-cart-orange);
    color: #fff;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 16px;
    font-weight: 950;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  cart-drawer.kpx-cart .cart__checkout-button::after,
  cart-drawer.kpx-cart .cart__checkout-button::before {
    display: none;
  }

  @media screen and (max-width: 749px) {
    cart-drawer.kpx-cart .drawer__inner {
      width: 100vw;
      border-left: 0;
    }

    cart-drawer.kpx-cart .drawer__header {
      padding: 16px 14px 9px;
    }

    cart-drawer.kpx-cart .drawer__heading {
      font-size: 23px;
    }

    .kpx-cart__stack {
      padding-left: 12px;
      padding-right: 12px;
    }

    .kpx-cart__item {
      grid-template-columns: 64px minmax(0, 1fr) auto;
      padding: 10px;
    }

    .kpx-cart__item-img {
      width: 64px;
      height: 64px;
    }

    .kpx-cart__timer span {
      font-size: 9px;
    }

    .kpx-cart__timer strong {
      font-size: 14px;
    }

    cart-drawer.kpx-cart .drawer__footer {
      padding: 13px 12px 14px;
    }
  }
`;

class KronoxCartDrawerGlobalEnhancer {
  constructor() {
    this.activeCartCurrency = window.Shopify?.currency?.active || 'USD';
    this.priorityThreshold = 15000;
    this.productCache = {};
    this.upgradeRequestId = 0;
    this.savingsRequestId = 0;
  }

  enhance() {
    this.injectStyles();
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    drawer.classList.add('kpx-cart');
    const inner = drawer.querySelector('.drawer__inner');
    if (!inner) return;

    this.ensureHeader(inner);
    this.ensureStack(inner);
    this.bindActions(drawer);
    this.startTimer();
    this.updateItems();
  }

  injectStyles() {
    if (document.getElementById('kpx-cart-drawer-global-styles')) return;
    const style = document.createElement('style');
    style.id = 'kpx-cart-drawer-global-styles';
    style.textContent = kpxCartDrawerStyles;
    document.head.appendChild(style);
  }

  ensureHeader(inner) {
    const header = inner.querySelector('.drawer__header');
    if (!header) return;

    const closeButton = header.querySelector('.drawer__close');
    const timer = header.querySelector('.kpx-cart__timer') || inner.querySelector('.kpx-cart__timer');

    if (timer && !header.contains(timer)) {
      if (closeButton) closeButton.insertAdjacentElement('beforebegin', timer);
      else header.appendChild(timer);
      return;
    }

    if (!timer) {
      const timerMarkup = '<div class="kpx-cart__timer"><span>Your order is reserved for</span><strong data-kpx-cart-timer>10:00</strong></div>';
      if (closeButton) closeButton.insertAdjacentHTML('beforebegin', timerMarkup);
      else header.insertAdjacentHTML('beforeend', timerMarkup);
    }
  }

  ensureStack(inner) {
    const footer = inner.querySelector('.drawer__footer');
    if (!footer || inner.querySelector('.kpx-cart__stack')) return;

    footer.insertAdjacentHTML('beforebegin', `
      <div class="kpx-cart__stack">
        <div class="kpx-cart__confirm"><i aria-hidden="true"></i><span>Added to cart</span></div>
        <div class="kpx-cart__panel kpx-cart__empty" data-kpx-cart-empty hidden>
          <strong>Your cart is empty</strong>
          <span>Add your Kronox pack to unlock the best offer.</span>
          <a class="kpx-cart__empty-button" href="/">Continue shopping</a>
        </div>
        <div class="kpx-cart__items" data-kpx-cart-items></div>
        <div class="kpx-cart__panel kpx-cart__upgrade" data-kpx-upgrade hidden>
          <div class="kpx-cart__upgrade-media" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none"><path d="M12 19V5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><path d="m6.5 10.5 5.5-5.5 5.5 5.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 19h14" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
          </div>
          <div>
            <span class="kpx-cart__eyebrow">Recommended upgrade</span>
            <strong data-kpx-upgrade-title>Unlock a better pack</strong>
            <p data-kpx-upgrade-text>Get more value before checkout.</p>
            <div class="kpx-cart__upgrade-bottom">
              <span class="kpx-cart__upgrade-price" data-kpx-upgrade-price>Upgrade now</span>
              <button type="button" class="kpx-cart__upgrade-button" data-kpx-cart-offer data-kpx-pack-upgrade disabled>Upgrade</button>
            </div>
          </div>
        </div>
        <div class="kpx-cart__panel kpx-cart__progress" data-kpx-priority-progress>
          <strong data-kpx-priority-title>Priority processing progress</strong>
          <p data-kpx-priority-text>You are ${this.formatMoney(0)} away from free priority processing.</p>
          <div class="kpx-cart__bar" aria-hidden="true"><span data-kpx-priority-bar></span></div>
          <div class="kpx-cart__milestones">
            <span data-kpx-priority-milestone class="is-active">Added</span>
            <span data-kpx-priority-milestone>Priority</span>
            <span data-kpx-priority-milestone>Complete</span>
          </div>
        </div>
        <div class="kpx-cart__trust" aria-label="Cart trust benefits">
          <div class="kpx-cart__trust-item"><svg viewBox="0 0 24 24" fill="none"><path d="M12 3 5 6v5c0 4.4 2.8 8.4 7 10 4.2-1.6 7-5.6 7-10V6l-7-3Z" stroke="currentColor" stroke-width="1.8"/><path d="m8.8 12.1 2 2 4.4-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg><span>45-Day Guarantee</span></div>
          <div class="kpx-cart__trust-item"><svg viewBox="0 0 24 24" fill="none"><path d="M3 7h11v9H3V7Zm11 3h4l3 3v3h-7v-6Z" stroke="currentColor" stroke-width="1.8"/><path d="M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm11 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" stroke="currentColor" stroke-width="1.8"/></svg><span>Fast Shipping</span></div>
          <div class="kpx-cart__trust-item"><svg viewBox="0 0 24 24" fill="none"><path d="M7 10V8a5 5 0 0 1 10 0v2" stroke="currentColor" stroke-width="1.8"/><path d="M5 10h14v10H5V10Z" stroke="currentColor" stroke-width="1.8"/><path d="M12 14v3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg><span>Secure Checkout</span></div>
        </div>
      </div>
    `);
  }

  bindActions(drawer) {
    if (drawer.dataset.kpxGlobalActionsBound === 'true') return;
    drawer.dataset.kpxGlobalActionsBound = 'true';

    drawer.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-kpx-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        this.removeItem(removeButton);
        return;
      }

      const upgradeButton = event.target.closest('[data-kpx-pack-upgrade]');
      if (upgradeButton) {
        if (upgradeButton.closest('.kpx-cart__stack')?.dataset.addonsSignature) return;
        event.preventDefault();
        this.upgradePack(upgradeButton);
      }
    });
  }

  startTimer() {
    if (window.kpxCartTimerStarted) return;
    window.kpxCartTimerStarted = true;
    window.kpxCartTimerEndsAt = Date.now() + 10 * 60 * 1000;

    const tick = () => {
      const remaining = Math.max(0, window.kpxCartTimerEndsAt - Date.now());
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      document.querySelectorAll('[data-kpx-cart-timer]').forEach((timer) => {
        timer.textContent = remaining > 0
          ? `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
          : 'Reservation expired';
      });
      if (!remaining) clearInterval(window.kpxCartTimerInterval);
    };

    tick();
    window.kpxCartTimerInterval = setInterval(tick, 1000);
  }

  updateItems() {
    const holder = document.querySelector('[data-kpx-cart-items]');
    if (!holder) return;
    if (holder.closest('.kpx-cart__stack')?.dataset.addonsSignature) return;

    fetch(`${window.routes?.cart_url || '/cart'}.js`)
      .then((response) => response.json())
      .then((cart) => {
        this.syncCurrency(cart);
        const items = cart.items || [];
        const drawer = document.querySelector('cart-drawer');
        const inner = drawer?.querySelector('.drawer__inner');
        const stack = drawer?.querySelector('.kpx-cart__stack');
        const empty = drawer?.querySelector('[data-kpx-cart-empty]');
        const footer = drawer?.querySelector('.drawer__footer');
        const confirm = drawer?.querySelector('.kpx-cart__confirm');
        const timer = drawer?.querySelector('.kpx-cart__timer');
        const modules = drawer?.querySelectorAll('[data-kpx-upgrade], [data-kpx-priority-progress], .kpx-cart__trust') || [];
        const isEmpty = !items.length;

        if (drawer) drawer.classList.toggle('is-empty', isEmpty);
        if (inner) inner.classList.toggle('is-empty', isEmpty);
        if (stack) stack.classList.toggle('is-empty', isEmpty);
        if (empty) empty.hidden = !isEmpty;
        holder.hidden = isEmpty;
        if (footer) footer.hidden = isEmpty;
        if (confirm) confirm.hidden = isEmpty;
        if (timer) timer.hidden = isEmpty;
        modules.forEach((module) => {
          module.hidden = isEmpty;
        });

        holder.innerHTML = items.map((item, index) => this.itemMarkup(item, index + 1)).join('');
        this.updateFooter(cart);
        this.updatePriority(cart);
        this.updateUpgrade(cart);
      })
      .catch(() => {});
  }

  itemMarkup(item, line) {
    const title = this.escapeHtml(item.product_title || item.title);
    const image = item.image
      ? `<img src="${this.escapeHtml(item.image)}" alt="${title}" class="kpx-cart__item-img" loading="lazy">`
      : '<div class="kpx-cart__item-img"></div>';
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : 'Selected pack';

    return `
      <div class="kpx-cart__panel kpx-cart__item" data-kpx-cart-line="${line}">
        ${image}
        <div class="kpx-cart__item-copy">
          <strong>${title}</strong>
          <span>${this.escapeHtml(variant)}</span>
        </div>
        <div class="kpx-cart__item-meta">
          <span class="kpx-cart__item-price">${this.formatMoney(item.final_line_price || item.line_price || item.price)}</span>
          <span class="kpx-cart__item-qty">Qty ${item.quantity}</span>
          <button type="button" class="kpx-cart__item-remove" data-kpx-cart-remove data-line="${line}" aria-label="Remove ${title}">Remove</button>
        </div>
      </div>
    `;
  }

  removeItem(button) {
    if (!button || button.disabled) return;
    const line = Number(button.dataset.line);
    if (!line) return;

    button.disabled = true;
    button.textContent = 'Removing...';

    const drawer = document.querySelector('cart-drawer');
    const payload = { line, quantity: 0 };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      payload.sections = drawer.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
      if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(button);
    }

    fetch(window.routes?.cart_change_url || '/cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message || 'Cart update failed');
        if (drawer && typeof drawer.renderContents === 'function' && response.sections) drawer.renderContents(response);
        setTimeout(() => this.enhance(), 80);
      })
      .catch((error) => {
        console.error(error);
        button.disabled = false;
        button.textContent = 'Remove';
      });
  }

  updateFooter(cart) {
    const drawer = document.querySelector('cart-drawer');
    const footer = drawer?.querySelector('.drawer__footer');
    const totals = footer?.querySelector('.totals');
    if (!footer || !totals) return;

    const checkoutButton = footer.querySelector('.cart__checkout-button');
    const isEmpty = !(cart?.items || []).length;
    if (checkoutButton) {
      checkoutButton.textContent = 'Checkout';
      checkoutButton.disabled = isEmpty;
      checkoutButton.setAttribute('aria-disabled', isEmpty ? 'true' : 'false');
    }

    const savings = this.calculateSavings(cart);

    let savingsRow = footer.querySelector('[data-kpx-cart-savings]');
    if (!savingsRow) {
      totals.insertAdjacentHTML('afterend', `<div class="kpx-cart__savings" data-kpx-cart-savings><span>Total savings</span><strong>${this.formatMoney(0)}</strong></div>`);
      savingsRow = footer.querySelector('[data-kpx-cart-savings]');
    }

    this.updateSavingsRow(savingsRow, savings);
    this.updateVariantCompareSavings(cart, savingsRow, savings);
  }

  updateSavingsRow(savingsRow, savings) {
    if (!savingsRow) return;
    const normalizedSavings = Math.max(0, Math.round(Number(savings) || 0));
    const value = savingsRow.querySelector('strong');
    savingsRow.hidden = normalizedSavings <= 0;
    if (value) value.textContent = this.formatMoney(normalizedSavings);
  }

  updateVariantCompareSavings(cart, savingsRow, baseSavings) {
    const requestId = ++this.savingsRequestId;
    const candidates = (cart?.items || [])
      .map((item) => ({ item, handle: this.itemHandle(item) }))
      .filter(({ handle }) => handle);

    if (!candidates.length) {
      this.updateSavingsRow(savingsRow, baseSavings);
      return;
    }

    Promise.all(
      candidates.map((candidate) => this.fetchProduct(candidate.handle)
        .then((product) => this.compareAtSavingsForItem(candidate.item, product))
        .catch(() => 0)),
    )
      .then((values) => {
        if (requestId !== this.savingsRequestId) return;
        const compareAtSavings = values.reduce((total, value) => total + Math.max(0, Number(value) || 0), 0);
        const finalSavings = Math.max(Number(baseSavings) || 0, compareAtSavings);
        this.updateSavingsRow(savingsRow?.isConnected ? savingsRow : document.querySelector('[data-kpx-cart-savings]'), finalSavings);
      })
      .catch(() => {
        if (requestId === this.savingsRequestId) this.updateSavingsRow(savingsRow, baseSavings);
      });
  }

  compareAtSavingsForItem(item, product) {
    const variants = product?.variants || [];
    const variant = variants.find((entry) => String(entry.id) === String(item.variant_id));
    const quantity = Math.max(1, this.toMoneyNumber(item.quantity) || 1);
    const compareAt = this.toMoneyNumber(variant?.compare_at_price);
    if (compareAt === null || compareAt <= 0) return 0;

    const finalLine = this.toMoneyNumber(item.final_line_price)
      ?? this.toMoneyNumber(item.line_price)
      ?? this.multiplyMoney(item.final_price, quantity)
      ?? this.multiplyMoney(item.price, quantity)
      ?? this.multiplyMoney(variant?.price, quantity);

    if (finalLine === null) return 0;
    return Math.max(0, compareAt * quantity - finalLine);
  }

  calculateSavings(cart) {
    const savingsCandidates = [];
    const totalDiscount = this.toMoneyNumber(cart?.total_discount);
    const originalTotal = this.toMoneyNumber(cart?.original_total_price);
    const finalTotal = this.toMoneyNumber(cart?.total_price);

    if (totalDiscount !== null) savingsCandidates.push(totalDiscount);
    if (originalTotal !== null && finalTotal !== null) savingsCandidates.push(originalTotal - finalTotal);

    let itemCompareSavings = 0;
    let itemDiscountSavings = 0;
    (cart?.items || []).forEach((item) => {
      const quantity = Math.max(1, this.toMoneyNumber(item.quantity) || 1);
      const originalLine = this.toMoneyNumber(item.original_line_price) ?? this.multiplyMoney(item.original_price, quantity);
      const finalLine = this.toMoneyNumber(item.final_line_price)
        ?? this.toMoneyNumber(item.line_price)
        ?? this.multiplyMoney(item.final_price, quantity)
        ?? this.multiplyMoney(item.price, quantity);
      const lineDiscount = this.toMoneyNumber(item.total_discount);

      if (originalLine !== null && finalLine !== null) itemCompareSavings += Math.max(0, originalLine - finalLine);
      if (lineDiscount !== null) itemDiscountSavings += Math.max(0, lineDiscount);
    });

    savingsCandidates.push(itemCompareSavings, itemDiscountSavings);
    return Math.max(0, ...savingsCandidates.map((value) => Math.round(Number(value) || 0)));
  }

  multiplyMoney(value, multiplier) {
    const amount = this.toMoneyNumber(value);
    return amount === null ? null : amount * multiplier;
  }

  toMoneyNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  updatePriority(cart) {
    const progress = document.querySelector('[data-kpx-priority-progress]');
    if (!progress) return;

    const total = Number(cart?.total_price || 0);
    const remaining = Math.max(0, this.priorityThreshold - total);
    const percent = Math.min(100, Math.round((total / this.priorityThreshold) * 100));
    const isUnlocked = total >= this.priorityThreshold;

    const title = progress.querySelector('[data-kpx-priority-title]');
    const text = progress.querySelector('[data-kpx-priority-text]');
    const bar = progress.querySelector('[data-kpx-priority-bar]');
    const milestones = progress.querySelectorAll('[data-kpx-priority-milestone]');

    if (title) title.textContent = isUnlocked ? 'Priority processing unlocked' : 'Priority processing progress';
    if (text) {
      text.textContent = isUnlocked
        ? 'Your order now qualifies for priority processing.'
        : `You are ${this.formatMoney(remaining)} away from free priority processing.`;
    }
    if (bar) bar.style.width = `${percent}%`;
    milestones.forEach((milestone, index) => {
      milestone.classList.toggle('is-active', index === 0 || (percent >= 50 && index === 1) || (isUnlocked && index === 2));
    });
  }

  updateUpgrade(cart) {
    const upgrade = document.querySelector('[data-kpx-upgrade]');
    if (!upgrade) return;
    if (upgrade.closest('.kpx-cart__stack')?.dataset.addonsSignature) return;

    const items = cart?.items || [];
    if (!items.length) {
      upgrade.hidden = true;
      return;
    }

    const requestId = ++this.upgradeRequestId;
    const candidates = items
      .map((item, index) => ({ item, line: index + 1, handle: this.itemHandle(item) }))
      .filter(({ handle }) => handle);

    if (!candidates.length) {
      upgrade.hidden = true;
      return;
    }

    Promise.all(
      candidates.map((candidate) => this.fetchProduct(candidate.handle)
        .then((product) => {
          const variants = product?.variants || [];
          const currentIndex = variants.findIndex((variant) => String(variant.id) === String(candidate.item.variant_id));
          const targetIndex = variants.findIndex((variant, index) => index > currentIndex && variant.available !== false);
          const target = variants[targetIndex];
          const current = variants[currentIndex];

          if (currentIndex < 0 || !target || !current) return null;
          return { ...candidate, current, currentIndex, target, targetIndex };
        })
        .catch(() => null)),
    )
      .then((matches) => {
        if (requestId !== this.upgradeRequestId) return;

        const match = matches.find(Boolean);
        if (!match) {
          upgrade.hidden = true;
          return;
        }

        const quantity = Math.max(1, Number(match.item.quantity || 1));
        const upgradeAmount = Math.max(0, (Number(match.target.price || 0) - Number(match.current.price || 0)) * quantity);
        const title = upgrade.querySelector('[data-kpx-upgrade-title]');
        const text = upgrade.querySelector('[data-kpx-upgrade-text]');
        const price = upgrade.querySelector('[data-kpx-upgrade-price]');
        const button = upgrade.querySelector('[data-kpx-pack-upgrade]');

        upgrade.hidden = false;
        if (title) title.textContent = `Unlock the ${match.target.title}`;
        if (text) {
          text.textContent = match.targetIndex === 1
            ? 'Get the straps and protection customers usually add later, now for less.'
            : 'Get a second full setup for gifting, backup, or sharing while the offer is active.';
        }
        if (price) price.textContent = upgradeAmount > 0 ? `Upgrade for ${this.formatMoney(upgradeAmount)}` : 'Upgrade now';
        if (button) {
          button.disabled = false;
          button.dataset.sourceLine = String(match.line);
          button.dataset.variantId = String(match.target.id);
          button.dataset.quantity = String(quantity);
          button.textContent = 'Upgrade';
        }
      })
      .catch(() => {
        upgrade.hidden = true;
      });
  }

  upgradePack(button) {
    if (!button || button.disabled) return;

    const sourceLine = Number(button.dataset.sourceLine || 0);
    const variantId = button.dataset.variantId;
    const quantity = Math.max(1, Number(button.dataset.quantity || 1));
    if (!sourceLine || !variantId) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Upgrading...';

    fetch(window.routes?.cart_change_url || '/cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ line: sourceLine, quantity: 0 }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message || 'Cart update failed');

        const drawer = document.querySelector('cart-drawer');
        const payload = { id: variantId, quantity };
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          payload.sections = drawer.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(button);
        }

        return fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
      })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');

        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function' && response.sections) drawer.renderContents(response);
        setTimeout(() => this.enhance(), 80);
      })
      .catch((error) => {
        console.error(error);
        button.disabled = false;
        button.textContent = originalText;
      });
  }

  itemHandle(item) {
    if (item.handle) return item.handle;
    const match = String(item.url || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  fetchProduct(handle) {
    if (this.productCache[handle]) return Promise.resolve(this.productCache[handle]);

    return fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then((response) => {
        if (!response.ok) throw new Error('Product lookup failed');
        return response.json();
      })
      .then((product) => {
        this.productCache[handle] = product;
        return product;
      });
  }

  syncCurrency(cart) {
    if (cart?.currency) this.activeCartCurrency = cart.currency;
    const footerText = document.querySelector('cart-drawer .drawer__footer')?.textContent || '';
    if (footerText.includes('€')) this.activeCartCurrency = 'EUR';
    if (footerText.includes('£')) this.activeCartCurrency = 'GBP';
  }

  formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    const template = document.querySelector('cart-drawer .totals__total-value')?.textContent?.trim()
      || document.querySelector('cart-drawer .drawer__footer .totals + *')?.textContent?.trim()
      || '';
    const symbol = {
      USD: '$', CAD: '$', AUD: '$', NZD: '$', SGD: '$', HKD: '$',
      EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', KRW: '₩', ILS: '₪', INR: '₹',
    }[this.activeCartCurrency];

    if (symbol && template.includes(symbol)) {
      const numericTemplate = template.match(/\d[\d.,\s]*/)?.[0]?.trim() || '';
      const lastComma = numericTemplate.lastIndexOf(',');
      const lastDot = numericTemplate.lastIndexOf('.');
      const decimalSeparator = lastComma > lastDot ? ',' : '.';
      const formattedValue = new Intl.NumberFormat(decimalSeparator === ',' ? 'de-DE' : 'en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
      const symbolBeforeAmount = template.indexOf(symbol) < template.search(/\d/);
      return symbolBeforeAmount ? `${symbol}${formattedValue}` : `${formattedValue} ${symbol}`;
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || navigator.language || 'en-US', {
        style: 'currency',
        currency: this.activeCartCurrency,
        currencyDisplay: 'narrowSymbol',
      }).format(value);
    } catch (error) {
      return `${this.activeCartCurrency} ${value.toFixed(2)}`;
    }
  }

  escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    })[character]);
  }
}

window.KronoxCartDrawerEnhancer = window.KronoxCartDrawerEnhancer || new KronoxCartDrawerGlobalEnhancer();

function localizeAnnouncementSavings() {
  const currency = window.Shopify?.currency?.active || 'USD';
  let amount = '150';

  try {
    amount = new Intl.NumberFormat(document.documentElement.lang || navigator.language || 'en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
    }).format(150);
  } catch (error) {
    amount = `${currency} 150`;
  }

  document.querySelectorAll('[class^="ai-announcement-highlight-"], [class*=" ai-announcement-highlight-"]').forEach((highlight) => {
    highlight.textContent = amount;
  });
}

function normalizeAlpineXProductContent() {
  if (!window.location.pathname.startsWith('/products/alpinex')) return;

  const currency = window.Shopify?.currency?.active || 'USD';
  const locale = document.documentElement.lang || navigator.language || 'en-US';
  const productMoneyText = document.querySelector('[data-kpx-price]')?.textContent || '';
  const moneyLocale = /,\d{2}/.test(productMoneyText) ? 'de-DE' : locale;
  const replacements = new Map([
    ['The Complete Pack includes the Alpinex SX5 smartwatch, 8 interchangeable straps, screen protection, magnetic charging cable, and KRONOX packaging.', 'The Complete Pack includes the AlpineX Smartwatch, 8 interchangeable straps, screen protection, a magnetic charging cable, and KRONOX packaging.'],
    ['Alpinex SX5 Smartwatch', 'AlpineX Smartwatch'],
    ['KRONOX gives you 45 days to try the any smartwatch. If it is not the right fit, contact support for return instructions.', 'Try your AlpineX Smartwatch for 45 days. If it is not the right fit, contact us for simple return instructions.'],
    ['3 Layer Screen Potector', '3 Layer Screen Protector'],
  ]);

  document.querySelectorAll('body *').forEach((element) => {
    if (element.children.length) return;
    const text = element.textContent?.trim();
    if (replacements.has(text)) element.textContent = replacements.get(text);
  });

  document.querySelectorAll('.kpx-product__complete-card > span, [class*="ai-bundle-pack-item-original-price-"]').forEach((element) => {
    const text = element.textContent?.trim() || '';
    const match = text.match(/^\$(\d+(?:\.\d+)?)(.*)$/);
    if (!match) return;

    const decimals = match[1].includes('.') ? match[1].split('.')[1].length : 0;
    try {
      const localizedValue = new Intl.NumberFormat(moneyLocale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(Number(match[1]));
      element.textContent = `${localizedValue}${match[2]}`;
    } catch (error) {
      element.textContent = `${currency} ${match[1]}${match[2]}`;
    }
  });

  document.querySelectorAll('a').forEach((link) => {
    if (link.textContent?.trim() === 'VIEW MORE FAQS' && !link.getAttribute('href')) {
      link.setAttribute('href', '/pages/faqs');
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  window.KronoxCartDrawerEnhancer.enhance();
  localizeAnnouncementSavings();
  normalizeAlpineXProductContent();
});
document.addEventListener('kpx:cart-drawer-opened', () => window.KronoxCartDrawerEnhancer.enhance());
document.addEventListener('shopify:section:load', () => {
  localizeAnnouncementSavings();
  normalizeAlpineXProductContent();
});

(function () {
  if (window.kpxGlobalCartLinkDrawerBound) return;
  window.kpxGlobalCartLinkDrawerBound = true;

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('a[href]');
      if (!trigger || trigger.closest('cart-drawer')) return;

      let url;
      try {
        url = new URL(trigger.getAttribute('href'), window.location.origin);
      } catch (error) {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (url.pathname.replace(/\/$/, '') !== '/cart') return;

      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (typeof drawer.open === 'function') {
        drawer.open(trigger);
      } else {
        drawer.classList.add('active');
        document.body.classList.add('overflow-hidden');
      }

      document.dispatchEvent(
        new CustomEvent('kpx:cart-drawer-opened', {
          detail: { trigger },
        }),
      );
    },
    true,
  );
})();
